{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://davisjam.github.io/model-based-agentic-software-engineering/workbench/mage-query.schema.json",
  "title": "MAGE query and query result",
  "description": "A question asked of a model system, and the shape of the answer. Every query carries an explicit quantifier: it determines what counts as evidence, and conflating existential with universal is the error the workbench exists to make visible. Semantics in SEMANTICS.md section 7.",
  "oneOf": [{ "$ref": "#/$defs/query" }, { "$ref": "#/$defs/result" }],

  "$defs": {
    "id": { "type": "string", "pattern": "^[A-Za-z_][A-Za-z0-9_.\\[\\]-]*$", "maxLength": 160 },

    "query": {
      "type": "object",
      "required": ["kind", "quantifier"],
      "additionalProperties": false,
      "properties": {
        "name": {
          "description": "Human-facing question. Saved queries re-run on model change.",
          "type": "string"
        },
        "kind": { "enum": ["behavior", "graph", "quantity"] },
        "expect": {
          "description": "Expected outcome, for a saved query used as a CI gate. Present means the query is an ASSERTION: a run whose outcome differs is a build failure. Absent means the query is exploratory and any outcome is reported without judgement. Values are holds/refuted/... and never true/false -- a bare boolean here is a YAML-coercion bug (V25).",
          "enum": ["holds", "refuted", "inconclusive", "unlicensed"]
        },
        "quantifier": {
          "description": "exists: a witness proves TRUE, exhaustive absence proves FALSE. forall: exhaustive satisfaction proves TRUE, a counterexample proves FALSE. Never inferred silently -- an ambiguous natural-language question triggers Clarification or a displayed interpretation (V21).",
          "enum": ["exists", "forall"]
        },
        "behavior": { "$ref": "#/$defs/behaviorQuery" },
        "graph": { "$ref": "#/$defs/graphQuery" },
        "quantity": { "$ref": "#/$defs/quantityQuery" }
      },
      "allOf": [
        {
          "if": { "properties": { "kind": { "const": "behavior" } } },
          "then": { "required": ["behavior"] }
        },
        {
          "if": { "properties": { "kind": { "const": "graph" } } },
          "then": { "required": ["graph"] }
        },
        {
          "if": { "properties": { "kind": { "const": "quantity" } } },
          "then": { "required": ["quantity"] }
        }
      ]
    },

    "behaviorQuery": {
      "description": "A question about executions. Safety and reachability only: fairness is unsupported, so 'can it reach X' is in scope and 'will it eventually reach X' is not.",
      "type": "object",
      "required": ["form"],
      "additionalProperties": false,
      "properties": {
        "form": {
          "description": "reach: a configuration satisfying target is reachable. invariant: predicate holds in every reachable configuration. recurrence: the target is RE-ENTERED -- a target configuration, at least one step, another target configuration; evidence is a LASSO whose cycle is the target-to-target segment. repeatable-cycle: a genuinely repeated CONFIGURATION, which is what makes a cycle actually repeatable; a loop whose bounded variable strictly advances answers refuted here while recurrence answers holds. Two forms, because can-it-come-back and can-it-loop-indefinitely are different questions and a query must denote a question rather than a search strategy. deadend: a configuration with no enabled step exists. transition-live: a named transition is executable in some reachable configuration.",
          "enum": ["reach", "invariant", "recurrence", "repeatable-cycle", "deadend", "transition-live"]
        },
        "target": {
          "description": "State predicate. Required for reach and recurrence.",
          "$ref": "#/$defs/predicate"
        },
        "predicate": {
          "description": "State predicate asserted to hold. Required for invariant.",
          "$ref": "#/$defs/predicate"
        },
        "avoid": {
          "description": "Configurations the trace must never pass through. A past-time question is NOT expressed here -- it compiles to an invariant over a history variable, and the compilation is disclosed (V23).",
          "$ref": "#/$defs/predicate"
        },
        "transition": {
          "type": "object",
          "additionalProperties": false,
          "properties": {
            "machine": { "$ref": "#/$defs/id" },
            "from": { "$ref": "#/$defs/id" },
            "to": { "$ref": "#/$defs/id" },
            "sync": { "$ref": "#/$defs/id" }
          }
        },
        "limit": {
          "description": "Max configurations to explore. Exceeding it yields coverage.kind=bounded, and therefore result=inconclusive (V22) -- never 'no'.",
          "type": "integer",
          "minimum": 1
        }
      }
    },

    "predicate": {
      "description": "Conjunction of atoms unless all-of/any-of/not is used. An atom's key names a machine state (document.state), an instance state (worker[1].state), a variable, or a derived value.",
      "oneOf": [
        {
          "type": "object",
          "minProperties": 1,
          "additionalProperties": {
            "oneOf": [
              { "type": ["string", "boolean", "integer"] },
              {
                "type": "object",
                "minProperties": 1,
                "additionalProperties": false,
                "properties": {
                  "eq": { "type": ["string", "boolean", "integer"] },
                  "ne": { "type": ["string", "boolean", "integer"] },
                  "lt": { "type": "integer" },
                  "le": { "type": "integer" },
                  "gt": { "type": "integer" },
                  "ge": { "type": "integer" }
                }
              }
            ]
          }
        },
        {
          "type": "object",
          "additionalProperties": false,
          "minProperties": 1,
          "properties": {
            "all-of": { "type": "array", "items": { "$ref": "#/$defs/predicate" } },
            "any-of": { "type": "array", "items": { "$ref": "#/$defs/predicate" } },
            "not": { "$ref": "#/$defs/predicate" }
          }
        }
      ]
    },

    "quantityQuery": {
      "description": "A quantitative question. There is deliberately NO aggregation parameter: the aggregation is DERIVED from the metric's dimension scope (SEMANTICS.md section 8) -- latency and cost are worst-case sums along executions, peak_memory is the maximum of memory(c) over reachable configurations -- so the max|min|named selector the section 29 refinement rejected cannot reappear here. `target` selects WHICH executions (those reaching it); `within` names the declared model:-targeted ceiling to decide against. A target on a configuration-scoped metric is a category error, refused by name. With `within` the quantifier must be forall (a ceiling claim is universal); without it, exists (a measurement is established by the witness that attains it). The computed figure travels on result.magnitude, dimension attached.",
      "type": "object",
      "required": ["metric"],
      "additionalProperties": false,
      "properties": {
        "metric": {
          "description": "The analysis, not a dimension: latency and cost account executions under the declared basis (V35); peak_memory is memory(c)'s reachable maximum. Kept in parity with REQUIREMENT_METRICS in src/quant/requirement.ts by test/quant-query.test.ts.",
          "enum": ["latency", "cost", "peak_memory"]
        },
        "target": {
          "description": "Execution-scoped metrics only: measure the executions REACHING a configuration satisfying this predicate. On a configuration-scoped metric this is a category error, refused rather than computed.",
          "$ref": "#/$defs/predicate"
        },
        "within": {
          "description": "The model:-targeted quantity declaring the ceiling to decide against. A name that resolves to no declared quantity is a V39 finding at authoring time and a refusal at ask time.",
          "$ref": "#/$defs/id"
        },
        "limit": {
          "description": "Max configurations to explore. Exceeding it yields coverage.kind=bounded, and therefore result=inconclusive (V22).",
          "type": "integer",
          "minimum": 1
        }
      }
    },

    "graphQuery": {
      "type": "object",
      "required": ["form", "relation"],
      "additionalProperties": false,
      "properties": {
        "form": {
          "enum": ["direct", "reachability", "path", "shortest-path", "all-paths", "predecessors", "successors", "cycles", "components", "containment"]
        },
        "relation": {
          "description": "Relation type traversed. A multi-hop form over a type with composition.path=forbidden is REFUSED, not answered (V7) -- outcome=unlicensed.",
          "$ref": "#/$defs/id"
        },
        "from": { "$ref": "#/$defs/id" },
        "to": { "$ref": "#/$defs/id" },
        "max-hops": { "type": "integer", "minimum": 1 },
        "where": {
          "description": "Join predicate over endpoint properties, e.g. source.classification > target.accepts. Well-typed only if both sides declare the SAME ordered domain (V20).",
          "type": "object",
          "additionalProperties": false,
          "properties": {
            "source": { "$ref": "#/$defs/propertyConstraint" },
            "target": { "$ref": "#/$defs/propertyConstraint" },
            "compare": {
              "type": "array",
              "items": {
                "type": "object",
                "required": ["left", "op", "right"],
                "additionalProperties": false,
                "properties": {
                  "left": { "type": "string" },
                  "op": { "enum": ["eq", "ne", "lt", "le", "gt", "ge"] },
                  "right": { "type": "string" }
                }
              }
            }
          }
        }
      }
    },

    "propertyConstraint": {
      "type": "object",
      "additionalProperties": {
        "oneOf": [
          { "type": ["string", "boolean", "integer"] },
          {
            "type": "object",
            "minProperties": 1,
            "additionalProperties": false,
            "properties": {
              "eq": { "type": ["string", "boolean", "integer"] },
              "ne": { "type": ["string", "boolean", "integer"] },
              "in": { "type": "array", "items": { "type": ["string", "boolean", "integer"] } }
            }
          }
        ]
      }
    },

    "result": {
      "type": "object",
      "required": ["outcome", "coverage"],
      "additionalProperties": false,
      "properties": {
        "outcome": {
          "description": "holds: the claim is established (a witness for exists, exhaustive satisfaction for forall). refuted: the claim is refuted (exhaustive absence for exists, a counterexample for forall). inconclusive: mandatory when coverage is bounded (V22). unlicensed: the model does not authorize the question -- a SUCCESSFUL result, not an error (V7, V24). Deliberately NOT true/false: there is no boolean result, only a claim plus coverage plus evidence -- and a bare `true`/`false` in YAML is implicit-typed to a boolean anyway (V25).",
          "enum": ["holds", "refuted", "inconclusive", "unlicensed"]
        },
        "interpreted-as": {
          "description": "The question actually evaluated, in plain language. Mandatory when the query came from natural language (V21).",
          "type": "string"
        },
        "formal-query": {
          "description": "The query object actually executed. The UI exposes this beside the answer.",
          "type": "object"
        },
        "coverage": {
          "type": "object",
          "required": ["kind"],
          "additionalProperties": false,
          "properties": {
            "kind": { "enum": ["exhaustive", "bounded", "not-applicable"] },
            "states_explored": { "type": "integer", "minimum": 0 },
            "reason": { "enum": ["state-limit", "time-limit", "depth-limit"] }
          },
          "allOf": [
            {
              "if": { "properties": { "kind": { "const": "bounded" } } },
              "then": { "required": ["reason"] }
            }
          ]
        },
        "evidence": { "$ref": "#/$defs/evidence" },
        "magnitude": {
          "description": "The figure a quantity query computed -- a worst-case total or a peak -- in the BASE unit of its dimension. The dimension travels WITH the number for the reason the RDF projection keeps it: a bare number lets a consumer add milliseconds to megabytes, which V30 forbids at the validation layer. Null/absent on non-quantitative results, and when a maximum is unbounded (the lasso evidence is the answer then).",
          "type": "object",
          "required": ["value", "dimension"],
          "additionalProperties": false,
          "properties": {
            "value": { "type": "number" },
            "dimension": { "enum": ["duration", "memory", "cost", "ratio", "count"] },
            "unit": {
              "description": "The dimension's base unit; null when the dimension is dimensionless.",
              "type": ["string", "null"]
            }
          }
        },
        "refusal": {
          "description": "Why the model does not license the question, as the sentence a person reads. Required when outcome=unlicensed. The same fact as data travels in refusalDetail; the two are written by one constructor and cannot disagree.",
          "type": "string"
        },
        "refusalDetail": { "$ref": "#/$defs/refusalDetail" },
        "compilation": {
          "description": "Disclosed rewrites, e.g. an auxiliary history variable added to answer a past-time question (V23).",
          "type": "array",
          "items": {
            "type": "object",
            "required": ["kind", "explanation"],
            "additionalProperties": false,
            "properties": {
              "kind": { "enum": ["history-variable", "symmetry-reduction", "vacuous", "other"] },
              "explanation": { "type": "string" }
            }
          }
        }
      },
      "allOf": [
        {
          "if": { "properties": { "outcome": { "const": "unlicensed" } } },
          "then": { "required": ["refusal"] }
        }
      ]
    },

    "evidence": {
      "description": "A witness supports an existential; a counterexample refutes a universal. A lasso is required for recurrence.",
      "type": "object",
      "required": ["shape", "role"],
      "additionalProperties": false,
      "properties": {
        "shape": { "enum": ["trace", "lasso", "path", "none"] },
        "role": { "enum": ["witness", "counterexample"] },
        "steps": { "type": "array", "items": { "$ref": "#/$defs/step" } },
        "cycle": {
          "description": "The repeating suffix. Required when shape=lasso.",
          "type": "array",
          "items": { "$ref": "#/$defs/step" }
        },
        "nodes": {
          "description": "Entity ids, for graph evidence.",
          "type": "array",
          "items": { "$ref": "#/$defs/id" }
        }
      },
      "allOf": [
        {
          "if": { "properties": { "shape": { "const": "lasso" } } },
          "then": { "required": ["cycle"] }
        }
      ]
    },

    "step": {
      "description": "One step of a behavioural trace. `from` and `to` are COMPLETE configurations, not deltas: `control` has an entry for every machine instance and `values` one for every `<instance>.<variable>`, so a reader can say which state the system was in at every step without replaying labels. Held against the live engine by test/evidence-configurations.test.ts. This definition previously promised a flat scalar map under a `machines` key while the product shipped `instances` and (worse) empty `{}` configurations -- both halves of that disagreement are fixed, and the test pins them.",
      "type": "object",
      "additionalProperties": false,
      "required": ["instances", "from", "to"],
      "properties": {
        "instances": {
          "description": "Instances that moved. More than one means a declared synchronized event fired; NONE means the checker's stutter step at a dead end (a device of the trace domain, not a transition anybody wrote).",
          "type": "array",
          "items": { "$ref": "#/$defs/id" }
        },
        "sync": { "oneOf": [{ "$ref": "#/$defs/id" }, { "type": "null" }] },
        "from": { "$ref": "#/$defs/stepConfiguration" },
        "to": { "$ref": "#/$defs/stepConfiguration" },
        "label": { "type": ["string", "null"] }
      }
    },

    "stepConfiguration": {
      "description": "One point in the state space, as evidence publishes it: every instance's control state under `control` (instance id -> state id), every variable's value under `values` (`<instance>.<variable>` -> value). Quantities, properties and derived values are deliberately NOT here -- a configuration is control plus variables and nothing else (section 6).",
      "type": "object",
      "additionalProperties": false,
      "required": ["control", "values"],
      "properties": {
        "control": { "type": "object", "additionalProperties": { "type": "string" } },
        "values": { "type": "object", "additionalProperties": { "type": ["string", "boolean", "integer"] } }
      }
    },

    "refusalReason": {
      "description": "The closed cause vocabulary every refusal carries (result.refusalDetail.reason, and the refusal arm of check()). A refusal is a SUCCESSFUL outcome (V7): composition-forbidden: the relation type declares composition.path forbidden (V7). missing-distinction: the model represents the subject but deliberately omits what the question needs (V24). unknown-vocabulary: the question names an entity, state, variable or relation this system does not declare. missing-model-type: the system declares no substrate of the model type the question interrogates. unsupported-form: the form exists in this schema but this version does not evaluate it. quantifier-mismatch: the declared quantifier asks for evidence the form cannot produce (section 7). unsupported-expression: an effect or derived expression outside the deliberately tiny grammars. reserved-feature: legal to write, reserved for a future version, refused rather than misread (V14/V15). category-error: the question pairs a quantity with the aggregation axis its scope does not have (section 8). Mirrors REFUSAL_REASONS in src/ir/types.ts; the parity is test-held in both directions.",
      "enum": [
        "composition-forbidden",
        "missing-distinction",
        "unknown-vocabulary",
        "missing-model-type",
        "unsupported-form",
        "quantifier-mismatch",
        "unsupported-expression",
        "reserved-feature",
        "category-error"
      ]
    },

    "refusalDetail": {
      "description": "The refusal's typed half: the cause as data, beside the sentence in result.refusal. An agent branches on reason; missing lists the distinctions the model would need in order to answer (empty when the gap is not a modeling one); models lists the models consulted while deciding, so a caller can say where to add the distinction.",
      "type": "object",
      "required": ["reason", "missing", "models"],
      "additionalProperties": false,
      "properties": {
        "reason": { "$ref": "#/$defs/refusalReason" },
        "missing": { "type": "array", "items": { "type": "string" } },
        "models": { "type": "array", "items": { "type": "string" } }
      }
    },

    "modelType": {
      "description": "The model-type registry's closed id vocabulary -- the modelType a check() report names in its licensed arm, and the type each query kind interrogates: structural-graph answers kind=graph (the graphQuery forms), state-machine answers kind=behavior (the behaviorQuery forms), quantitative-model answers kind=quantity (the quantityQuery metrics).",
      "enum": ["structural-graph", "state-machine", "quantitative-model"]
    },

    "binding": {
      "description": "The closed binding-name vocabulary: every declared correspondence between elements in different purposeful models, and the names the composed view's focus accepts. appears-in: one entity's identity across the purposeful models that mention it (entity-model). machine-of-entity: this behavioural model describes this structural entity (machine-entity). state-of-entity: an entity property naming the lifecycle state during whose occupancy it runs (entity-state).",
      "enum": ["appears-in", "machine-of-entity", "state-of-entity"]
    },

    "querySubject": {
      "description": "What a question may be ABOUT, per model type: the registry's closed {noun, selector} pairs. The selector names the grammar that picks instances of the noun out: by-id takes a declared identifier; property-constraints takes a {property: value | {ne} | {in: []}} object; predicate takes a predicate over machine state; selector takes a partial match on declared transition fields (machine/from/to/sync). A name outside the declared vocabulary is an invalid question, not evidence of absence.",
      "oneOf": [
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "structural-graph" }, "noun": { "const": "entity" }, "selector": { "const": "property-constraints" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "structural-graph" }, "noun": { "const": "relation type" }, "selector": { "const": "by-id" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "structural-graph" }, "noun": { "const": "model" }, "selector": { "const": "by-id" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "state-machine" }, "noun": { "const": "machine" }, "selector": { "const": "by-id" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "state-machine" }, "noun": { "const": "state" }, "selector": { "const": "predicate" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "state-machine" }, "noun": { "const": "variable" }, "selector": { "const": "predicate" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "state-machine" }, "noun": { "const": "transition" }, "selector": { "const": "selector" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "quantitative-model" }, "noun": { "const": "quantity" }, "selector": { "const": "by-id" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "quantitative-model" }, "noun": { "const": "execution" }, "selector": { "const": "predicate" } } },
        { "type": "object", "required": ["modelType", "noun", "selector"], "additionalProperties": false, "properties": { "modelType": { "const": "quantitative-model" }, "noun": { "const": "ceiling" }, "selector": { "const": "by-id" } } }
      ]
    }
  }
}
